import type { StatusSugestao } from './sugestoes';
import { api } from './cliente';
import type { RegrasMonetizacao } from '../../shared/monetizacao';
import type { PermissoesAdmin } from '../../shared/permissoes-admin';
import type { EditarAdmin, NovoAdmin } from '../../shared/schemas/admin';

/**
 * Cliente da API administrativa.
 *
 * Plano separado do marketplace: cookie próprio (`__Host-chamaum_admin`),
 * tabela própria (`admins_plataforma`) e TOTP obrigatório. Uma sessão de
 * marketplace não abre nenhuma rota daqui, e vice-versa — isso é conferido no
 * servidor e coberto pela matriz de autorização.
 */
export interface Admin {
  id: string;
  nome: string;
  email: string;
  papel: 'operador' | 'supervisor' | 'dono';
  permissoes: PermissoesAdmin;
}

export interface ParecerIa {
  decisao: 'aprovar' | 'recusar' | 'humano';
  confianca: number;
  motivo: string;
  riscos: string[];
  criado_em: number;
}

export interface ItemDaFila {
  id: string;
  usuario_id: string;
  criado_em: number;
  nome_exibicao: string;
  slug: string;
  perfil_status: string;
  email: string;
  telefone: string | null;
  qtd_certificacoes: number;
  qtd_documentos: number;
  qtd_trabalhos: number;
  parecer_ia: ParecerIa | null;
}

export interface Dossie {
  perfil: Record<string, unknown> & {
    usuario_id: string; nome_exibicao: string; slug: string; bio: string | null;
    status: string; email: string; telefone: string | null;
    anos_experiencia: number | null; conta_criada_em: number;
  };
  certificacoes: {
    id: string; tipo: string; nome: string; emissor: string | null;
    numero: string | null; emitida_em: number | null; valida_ate: number | null;
    arquivo_id: string | null; status: string; motivo: string | null;
  }[];
  documentos: {
    id: string; tipo: string; arquivo_id: string;
    documento_ultimos4: string | null; criado_em: number;
  }[];
  categorias: { nome: string; exige_certificacao: number; certificacao_exigida: string | null }[];
  areas: { nome: string; uf: string }[];
  trabalhos: { id: string; titulo: string; realizado_em: number | null }[];
  historico: { status: string; motivo: string | null; criado_em: number; decidido_em: number | null }[];
  /** Conferências que o revisor faria a olho, feitas pelo servidor. */
  pendencias: string[];
  /** O que a validação automática concluiu, quando já rodou. */
  parecer_ia: ParecerIa | null;
}

export const adminEuQuery = {
  queryKey: ['admin', 'eu'] as const,
  queryFn: async () => {
    try {
      return (await api.get<{ admin: Admin }>('/admin/eu')).admin;
    } catch {
      return null;
    }
  },
  retry: false,
  staleTime: 30_000,
};

export const filaQuery = {
  queryKey: ['admin', 'fila'] as const,
  queryFn: () => api.get<{ fila: ItemDaFila[] }>('/admin/fila/verificacoes'),
};

export const dossieQuery = (usuarioId: string) => ({
  queryKey: ['admin', 'verificacao', usuarioId] as const,
  queryFn: () => api.get<Dossie>(`/admin/verificacoes/${usuarioId}`),
});

/**
 * Uma alteração de perfil esperando decisão.
 *
 * `proposta` e `anterior` já vêm abertos pelo servidor, para a tela mostrar
 * antes/depois sem interpretar string.
 */
export interface CamposDoPerfil {
  nome_exibicao?: string;
  bio?: string | null;
  categorias?: string[];
}

export interface AlteracaoDaFila {
  id: string;
  usuario_id: string;
  criado_em: number;
  nome_exibicao: string;
  slug: string;
  email: string;
  proposta: CamposDoPerfil;
  anterior: CamposDoPerfil;
  parecer_ia: ParecerIa | null;
}

export const filaAlteracoesQuery = {
  queryKey: ['admin', 'fila-alteracoes'] as const,
  queryFn: () => api.get<{ fila: AlteracaoDaFila[] }>('/admin/fila/alteracoes'),
};

export const aprovarAlteracao = (id: string, motivo?: string) =>
  api.post<{ ok: true }>(`/admin/alteracoes/${id}/aprovar`, { motivo });

export const recusarAlteracao = (id: string, motivo: string) =>
  api.post<{ ok: true }>(`/admin/alteracoes/${id}/recusar`, { motivo });

export const entrarAdmin = (d: { email: string; senha: string; codigo: string }) =>
  api.post<{ admin: Pick<Admin, 'nome' | 'papel'> }>('/admin/login', d);

export const sairAdmin = () => api.post<void>('/admin/sair');

export const aprovar = (usuarioId: string, motivo?: string) =>
  api.post<{ ok: true }>(`/admin/verificacoes/${usuarioId}/aprovar`, { motivo });

export const recusar = (usuarioId: string, motivo: string) =>
  api.post<{ ok: true }>(`/admin/verificacoes/${usuarioId}/recusar`, { motivo });

export const decidirCertificacao = (
  id: string, decisao: 'aprovada' | 'recusada', motivo?: string,
) => api.post<{ ok: true }>(`/admin/certificacoes/${id}/decidir`, { decisao, motivo });

export const trocarSenhaAdmin = (d: {
  senha_atual: string; senha_nova: string; codigo: string;
}) => api.post<{ ok: true; mensagem: string }>('/admin/trocar-senha', d);

export interface SituacaoChave {
  chave: string;
  titulo: string;
  ajuda: string;
  grupo: 'email' | 'stripe' | 'asaas' | 'turnstile' | 'ia' | 'whatsapp' | 'push' | 'suporte';
  segredo: boolean;
  origem: 'ambiente' | 'painel' | 'vazio';
  /** Máscara, ou o valor inteiro quando não é segredo. Nunca o segredo. */
  amostra: string | null;
  atualizado_em: number | null;
}

export const configuracaoQuery = {
  queryKey: ['admin', 'configuracao'] as const,
  queryFn: () => api.get<{ configuracao: SituacaoChave[] }>('/admin/configuracao'),
};

export const gravarConfiguracao = (chave: string, valor: string) =>
  api.put<{ ok: true; aviso: string | null }>(`/admin/configuracao/${chave}`, { valor });

export const apagarConfiguracao = (chave: string) =>
  api.del<void>(`/admin/configuracao/${chave}`);

export const testarEmail = (para: string) =>
  api.post<{ ok: boolean; mensagem: string; codigo?: string }>(
    '/admin/configuracao/testar-email', { para },
  );

export interface StatusWhatsApp {
  status: 'idle' | 'sleeping' | 'connecting' | 'qr' | 'pairing'
    | 'connected' | 'disconnected' | 'banned' | 'desconhecido';
  qr: string | null;
  qrImageUrl: string | null;
  phoneNumber: string | null;
  name: string | null;
}

export interface PainelWhatsApp {
  configurado: boolean;
  sessao: string;
  webhook?: string;
  status: StatusWhatsApp | null;
}

export const whatsappAdminQuery = {
  queryKey: ['admin', 'whatsapp'] as const,
  queryFn: () => api.get<PainelWhatsApp>('/admin/whatsapp'),
};

export const conectarWhatsApp = () =>
  api.post<{ status: StatusWhatsApp }>('/admin/whatsapp/conectar');
export const desconectarWhatsApp = () => api.post<{ ok: true }>('/admin/whatsapp/desconectar');
export const testarWhatsApp = (telefone: string) =>
  api.post<{ ok: boolean; mensagem: string; codigo?: string }>(
    '/admin/whatsapp/testar', { telefone },
  );

export interface ResultadoWebhook {
  ok: boolean;
  url: string;
  mensagem: string;
  /** Vem quando já existe um endpoint para a mesma URL e a troca precisa de aval. */
  precisa_confirmar?: boolean;
  eventos?: number;
  aviso?: string | null;
}

export const gerarWebhookStripe = (substituir = false) =>
  api.post<ResultadoWebhook>('/admin/configuracao/gerar-webhook-stripe', { substituir });

// ---------------------------------------------------------------------------
// Monetização
//
// Só o papel `dono`. Salvar cria uma VERSÃO — o histórico é imutável e a
// pergunta "quanto estava valendo em março?" tem resposta.
// ---------------------------------------------------------------------------

export interface DiferencaRegra { campo: string; de: string; para: string }

export interface VersaoMonetizacao {
  versao: number;
  resumo: string;
  vigente_de: number;
  por: string;
  diff: DiferencaRegra[];
}

export const monetizacaoQuery = {
  queryKey: ['admin', 'monetizacao'] as const,
  queryFn: () => api.get<{
    regras: RegrasMonetizacao; versoes: VersaoMonetizacao[];
  }>('/admin/monetizacao'),
};

export const gravarMonetizacao = (regras: Omit<RegrasMonetizacao, 'versao'>) =>
  api.put<{ ok: true; versao: number; resumo: string; diff: DiferencaRegra[] }>(
    '/admin/monetizacao', regras,
  );

export interface PlanoAdmin {
  id: string;
  nome: string;
  descricao: string | null;
  preco_mensal_centavos: number;
  taxa_bps: number | null;
  beneficios: string[];
  destaque_slots_mes: number;
  ordem: number;
  ativo: number;
  stripe_price_id: string | null;
  stripe_product_id: string | null;
  tipo: 'publico' | 'parceiro';
  taxa_pix_bps: number | null;
  taxa_pix_fixa_centavos: number | null;
  taxa_cartao_bps: number | null;
  taxa_cartao_fixa_centavos: number | null;
}

export const planosQuery = {
  queryKey: ['admin', 'planos'] as const,
  queryFn: () => api.get<{ planos: PlanoAdmin[] }>('/admin/monetizacao/planos'),
};

export const gravarPlano = (p: Omit<PlanoAdmin,
  'ativo' | 'stripe_price_id' | 'stripe_product_id' | 'descricao' | 'tipo'
  | 'taxa_pix_bps' | 'taxa_pix_fixa_centavos' | 'taxa_cartao_bps' | 'taxa_cartao_fixa_centavos'>
  & { descricao?: string }
  & Partial<Pick<PlanoAdmin,
    'taxa_pix_bps' | 'taxa_pix_fixa_centavos' | 'taxa_cartao_bps' | 'taxa_cartao_fixa_centavos'>>) =>
  api.post<{ ok: true; plano: PlanoAdmin }>('/admin/monetizacao/planos', p);

export const publicarPlano = (id: string) =>
  api.post<{ ok: true; price_id: string }>(`/admin/monetizacao/planos/${id}/publicar`, {});

export const aposentarPlano = (id: string) =>
  api.post<{ ok: true }>(`/admin/monetizacao/planos/${id}/aposentar`, {});

export const sincronizarWebhook = () =>
  api.post<{ ok: true; eventos: number; mensagem: string }>(
    '/admin/configuracao/sincronizar-webhook', {},
  );

// ---------------------------------------------------------------------------
// Contestações
//
// Decidir uma contestação move dinheiro que já está com a plataforma e é de um
// dos dois lados. A fila vem em ordem de chegada e a decisão escrita é
// obrigatória — é ela que as duas partes recebem por e-mail.
// ---------------------------------------------------------------------------

export interface DisputaDaFila {
  id: string;
  chamado_id: string;
  pagamento_id: string | null;
  aberta_por: 'cliente' | 'profissional';
  motivo: string;
  status: 'aberta' | 'em_analise';
  criado_em: number;
  titulo: string;
  chamado_status: string;
  bruto_centavos: number | null;
  liquido_centavos: number | null;
  pagamento_status: string | null;
  cliente_nome: string;
  profissional_nome: string | null;
  /** Prazo de 5 dias úteis para decidir. */
  vence_em: number;
  atrasada: boolean;
  /** Desfecho do chargeback no banco ('won', 'lost'...), quando houve. */
  stripe_status: string | null;
}

export const filaDisputasQuery = {
  queryKey: ['admin', 'fila-disputas'] as const,
  queryFn: () => api.get<{ fila: DisputaDaFila[] }>('/admin/fila/disputas'),
};

// ---------------------------------------------------------------------------
// Fila de prazos: o que o cron de prazos não resolve sozinho.
// ---------------------------------------------------------------------------

export interface PrazoDaFila {
  id: string;
  chamado_id: string;
  etapa: 'sem_inicio.equipe' | 'parado.equipe';
  motivo: string;
  criado_em: number;
  titulo: string;
  chamado_status: string;
  cliente_nome: string;
  profissional_nome: string | null;
  pagamento_status: string | null;
  bruto_centavos: number | null;
}

export const filaPrazosQuery = {
  queryKey: ['admin', 'fila-prazos'] as const,
  queryFn: () => api.get<{ fila: PrazoDaFila[] }>('/admin/prazos'),
};

// ---------------------------------------------------------------------------
// Números do marketplace: funil do profissional e cobertura por cidade.
// ---------------------------------------------------------------------------

export interface MetricasMarketplace {
  dias: number;
  funil: { cadastrou: number; enviou: number; verificado: number; recebimento: number; orcou: number; concluiu: number };
  pedidos: {
    total: number; sem_convite: number; com_orcamento: number; concluidos: number;
    espera_mediana_s: number | null; espera_p90_s: number | null;
  };
  cobertura: {
    municipio_ibge: string; municipio_nome: string; uf: string;
    categoria_id: string; categoria_nome: string; profissionais: number; pedidos: number;
  }[];
  sem_convite: { municipio_nome: string; uf: string; categoria_nome: string; pedidos: number }[];
}

export const metricasQuery = (dias: number) => ({
  queryKey: ['admin', 'metricas', dias] as const,
  queryFn: () => api.get<MetricasMarketplace>(`/admin/metricas?dias=${dias}`),
});

export const resolverPrazo = (id: string, nota: string) =>
  api.post<{ ok: true }>(`/admin/prazos/${id}/resolver`, { nota });

export const decidirDisputa = (
  id: string, resultado: 'cliente' | 'profissional', decisao: string,
) => api.post<{ ok: true; reembolso_centavos: number | null }>(
  `/admin/disputas/${id}/decidir`, { resultado, decisao },
);

// ---------------------------------------------------------------------------
// Denúncias
// ---------------------------------------------------------------------------

export interface DenunciaDaFila {
  id: string;
  autor_id: string;
  autor_nome: string;
  alvo_tipo: string;
  alvo_id: string;
  motivo: string;
  descricao: string | null;
  status: string;
  criado_em: number;
  prioridade: 'normal' | 'alta' | 'urgente';
  risco_imediato: number;
  triagem_status: 'pendente' | 'processando' | 'concluida' | 'indisponivel';
  triagem_resumo: string | null;
  triagem_sinais: string | null;
  /** Mensagens do denunciado com contato fora do app, em 90 dias. */
  sinais_contato_90d?: number;
  /** Preenchido quando dá para dizer de quem se trata pelo alvo. */
  alvo_nome: string | null;
  alvo_usuario_id: string | null;
}

export const filaDenunciasQuery = {
  queryKey: ['admin', 'fila-denuncias'] as const,
  queryFn: () => api.get<{ fila: DenunciaDaFila[] }>('/admin/fila/denuncias'),
};

export const decidirDenuncia = (
  id: string, resultado: 'procedente' | 'improcedente', decisao: string,
) => api.post<{ ok: true }>(`/admin/denuncias/${id}/decidir`, { resultado, decisao });

export interface FotoAvaliacaoDaFila {
  id: string;
  arquivo_id: string;
  alt_texto: string;
  criado_em: number;
  nota: number;
  comentario: string | null;
  profissional_nome: string;
}

export const filaFotosAvaliacaoQuery = {
  queryKey: ['admin', 'fila-avaliacao-fotos'] as const,
  queryFn: () => api.get<{ fila: FotoAvaliacaoDaFila[] }>('/admin/fila/avaliacao-fotos'),
};

export const decidirFotoAvaliacao = (id: string, resultado: 'aprovada' | 'recusada', motivo?: string) =>
  api.post<{ ok: true }>(`/admin/avaliacao-fotos/${id}/decidir`, { resultado, motivo });

export const iniciarAnaliseDenuncia = (id: string) =>
  api.post<{ ok: true }>(`/admin/denuncias/${id}/iniciar-analise`, {});

export const bloquearContatoDenuncia = (id: string) =>
  api.post<{ ok: true }>(`/admin/denuncias/${id}/bloquear-contato`, {});

export interface MensagemContextoDenuncia {
  id: string;
  tipo: string;
  corpo: string | null;
  criado_em: number;
  autor_papel: 'cliente' | 'profissional' | 'sistema';
}

export const contextoDenunciaQuery = (id: string) => ({
  queryKey: ['admin', 'denuncia-contexto', id] as const,
  queryFn: () => api.get<{ mensagens: MensagemContextoDenuncia[] }>(`/admin/denuncias/${id}/contexto`),
  enabled: false,
});

// ---------------------------------------------------------------------------
// Profissionais: plano parceiro
//
// Só o papel `dono`. Tornar parceiro cancela na hora a mensalidade que o
// profissional pagasse na Stripe, sem reembolso automático.
// ---------------------------------------------------------------------------

export interface ProfissionalAdmin {
  usuario_id: string;
  nome_exibicao: string;
  email: string;
  telefone: string | null;
  status: string;
  plano_id: string | null;
  plano_nome: string | null;
  plano_tipo: 'publico' | 'parceiro' | null;
  assinatura_status: string | null;
  motivo_status: string | null;
  qtd_cancelamentos_90d: number;
  debitos_pendentes: number;
  debitos_pendentes_centavos: number;
}

export const profissionaisAdminQuery = (q: string) => ({
  queryKey: ['admin', 'profissionais', q] as const,
  queryFn: () => api.get<{ profissionais: ProfissionalAdmin[]; lista_padrao: boolean }>(
    `/admin/profissionais?q=${encodeURIComponent(q)}`,
  ),
});

export interface DetalheProfissionalAdmin {
  profissional: ProfissionalAdmin;
  assinatura: {
    plano_id: string; plano_tipo: 'publico' | 'parceiro'; status: string;
    periodo_fim: number | null; cobranca_stripe: boolean;
  } | null;
  parceiro: boolean;
}

export const profissionalAdminQuery = (id: string) => ({
  queryKey: ['admin', 'profissional', id] as const,
  queryFn: () => api.get<DetalheProfissionalAdmin>(`/admin/profissionais/${id}`),
});

export const tornarParceiro = (id: string, motivo: string) =>
  api.post<{ ok: true; assinatura_stripe_cancelada: boolean }>(
    `/admin/profissionais/${id}/parceiro`, { motivo },
  );

export const removerParceiro = (id: string, motivo: string) =>
  api.del<{ ok: true }>(`/admin/profissionais/${id}/parceiro`, { motivo });

export const reativarProfissional = (id: string, motivo: string) =>
  api.post<{ ok: true }>(`/admin/profissionais/${id}/reativar`, { motivo });

export const perdoarDebitos = (id: string, motivo: string) =>
  api.post<{ ok: true; perdoados: number; total_centavos: number }>(
    `/admin/profissionais/${id}/debitos/perdoar`, { motivo },
  );

// Fila de garantias: pedidos de retorno que a equipe acompanha.
export interface GarantiaDaFila {
  id: string; chamado_id: string; descricao: string; criado_em: number;
  titulo: string; confirmado_em: number | null;
  cliente_nome: string; profissional_nome: string | null;
}

export const filaGarantiasQuery = {
  queryKey: ['admin', 'fila-garantias'] as const,
  queryFn: () => api.get<{ fila: GarantiaDaFila[] }>('/admin/garantias'),
};

export const decidirGarantia = (id: string, status: 'resolvida' | 'recusada', decisao: string) =>
  api.post<{ ok: true }>(`/admin/garantias/${id}/decidir`, { status, decisao });


// ── Usuários do painel (só o dono) ──────────────────────────────────────────

export interface UsuarioPainel {
  id: string; nome: string; email: string; papel: Admin['papel']; ativo: boolean;
  permissoes: PermissoesAdmin; tem_autenticador: boolean; convite_pendente: boolean; criado_em: number;
}
export interface LinkConvite { link: string; email_enviado: boolean }

export const usuariosPainelQuery = {
  queryKey: ['admin', 'usuarios'] as const,
  queryFn: async () => (await api.get<{ usuarios: UsuarioPainel[] }>('/admin/usuarios')).usuarios,
};
export const criarUsuarioPainel = (d: NovoAdmin) => api.post<LinkConvite & { id: string }>('/admin/usuarios', d);
export const editarUsuarioPainel = (id: string, d: EditarAdmin) => api.put<{ ok: true }>(`/admin/usuarios/${encodeURIComponent(id)}`, d);
export const novoAcessoUsuarioPainel = (id: string) => api.post<LinkConvite>(`/admin/usuarios/${encodeURIComponent(id)}/convite`);

export const abrirConvite = (token: string) =>
  api.post<{ nome: string; email: string; segredo: string; uri: string }>('/admin/convite/abrir', { token });
export const concluirConvite = (d: { token: string; senha: string; codigo: string }) =>
  api.post<{ ok: true; mensagem: string }>('/admin/convite/concluir', d);

// ── Suporte (atendimentos da Ana e base de conhecimento do Chama Um) ───────

export interface AtendimentoFila {
  id: string; codigo: string; canal: string; estado: 'ia' | 'aguardando_humano' | 'humano' | 'encerrado'; perfil?: string;
  cliente_nome: string | null; resumo: string | null; motivo_handoff: string | null;
  responsavel: string | null; ultima_mensagem: string | null; ultima_em: number | null; atualizado_em: number;
}
export interface DetalheAtendimento {
  atendimento: {
    id: string; codigo: string; canal: string; estado: AtendimentoFila['estado']; resumo: string | null;
    motivo_handoff: string | null; csat: number | null; iniciado_em: number; atualizado_em: number;
    encerrado_em: number | null; responsavel: string | null; cliente: { nome: string | null; email: string | null } | null;
    perfil: string; custo_micro_usd: number; nivel: string | null;
  };
  mensagens: { id: string; autor: 'cliente' | 'ana' | 'operador' | 'sistema'; autor_nome: string | null; tipo: string; conteudo: string; criado_em: number }[];
}

export const suporteEstadoQuery = {
  queryKey: ['admin', 'suporte', 'estado'] as const,
  queryFn: () => api.get<{ configurado: boolean; n2: boolean; aviso_equipe: boolean; eventos_pendentes: number }>('/admin/suporte/estado'),
};
export const suporteFilaQuery = {
  queryKey: ['admin', 'suporte', 'fila'] as const,
  queryFn: () => api.get<{ esperando: AtendimentoFila[]; com_ana: AtendimentoFila[] }>('/admin/suporte/fila'),
  refetchInterval: 15_000,
};
export const suporteEncerradosQuery = {
  queryKey: ['admin', 'suporte', 'encerrados'] as const,
  queryFn: async () => (await api.get<{ atendimentos: AtendimentoFila[] }>('/admin/suporte/encerrados')).atendimentos,
};
export const suporteAtendimentoQuery = (id: string) => ({
  queryKey: ['admin', 'suporte', 'atendimento', id] as const,
  queryFn: () => api.get<DetalheAtendimento>(`/admin/suporte/atendimentos/${encodeURIComponent(id)}`),
  refetchInterval: 10_000,
});
export const responderAtendimento = (id: string, texto: string) =>
  api.post<{ ok: true }>(`/admin/suporte/atendimentos/${encodeURIComponent(id)}/responder`, { texto });
export const acaoAtendimento = (id: string, acao: 'assumir' | 'devolver' | 'encerrar') =>
  api.post<{ ok: true }>(`/admin/suporte/atendimentos/${encodeURIComponent(id)}/${acao}`);

export const conhecimentoQuery = {
  queryKey: ['admin', 'suporte', 'conhecimento'] as const,
  queryFn: async () => (await api.get<{ documentos: { nome: string; tamanho: number; enviado: number; origem: 'repo' | 'painel' }[] }>('/admin/suporte/conhecimento')).documentos,
};
export const documentoQuery = (nome: string) => ({
  queryKey: ['admin', 'suporte', 'conhecimento', nome] as const,
  queryFn: async () => (await api.get<{ conteudo: string }>(`/admin/suporte/conhecimento/${encodeURIComponent(nome)}`)).conteudo,
});
export const salvarDocumento = (nome: string, conteudo: string) =>
  api.put<{ ok: true }>(`/admin/suporte/conhecimento/${encodeURIComponent(nome)}`, { conteudo });
export const apagarDocumento = (nome: string) =>
  api.del<{ ok: true }>(`/admin/suporte/conhecimento/${encodeURIComponent(nome)}`);

export type EstadoSolicitacaoAdmin = 'aberta' | 'em_andamento' | 'aguardando_pessoa' | 'resolvida' | 'fechada';
export interface SolicitacaoFila {
  id: string; numero: number; assunto: string; categoria: string; prioridade: 'normal' | 'alta';
  estado: EstadoSolicitacaoAdmin; estado_rotulo: string; criado_em: number; atualizado_em: number;
  pessoa_nome: string; responsavel: string | null;
}
export interface DetalheSolicitacao {
  solicitacao: SolicitacaoFila & { descricao: string; atendimento_id: string | null; pessoa: { nome: string; email: string } | null };
  eventos: { id: string; tipo: string; para_estado: string | null; texto: string | null; criado_em: number; autor_tipo: string; autor_nome: string | null }[];
}
export const solicitacoesAdminQuery = (estado?: EstadoSolicitacaoAdmin) => ({
  queryKey: ['admin', 'suporte', 'solicitacoes', estado ?? 'abertas'] as const,
  queryFn: async () => (await api.get<{ solicitacoes: SolicitacaoFila[] }>(`/admin/suporte/solicitacoes${estado ? `?estado=${estado}` : ''}`)).solicitacoes,
  refetchInterval: 30_000,
});
export const solicitacaoAdminQuery = (id: string) => ({
  queryKey: ['admin', 'suporte', 'solicitacao', id] as const,
  queryFn: () => api.get<DetalheSolicitacao>(`/admin/suporte/solicitacoes/${encodeURIComponent(id)}`),
});
export const mudarEstadoSolicitacao = (id: string, estado: Exclude<EstadoSolicitacaoAdmin, 'aberta'>, texto?: string) =>
  api.post<{ ok: true }>(`/admin/suporte/solicitacoes/${encodeURIComponent(id)}/status`, { estado, texto });
export const responderSolicitacao = (id: string, texto: string) =>
  api.post<{ ok: true }>(`/admin/suporte/solicitacoes/${encodeURIComponent(id)}/responder`, { texto });
export const notaSolicitacao = (id: string, texto: string) =>
  api.post<{ ok: true }>(`/admin/suporte/solicitacoes/${encodeURIComponent(id)}/nota`, { texto });

export interface MetricasSuporte {
  dias: number; total: number | null; pela_ana: number | null; com_equipe: number | null;
  csat_media: number | null; csat_respostas: number | null; espera_media_s: number | null;
  custo_micro_usd: number | null; latencia_media_ms: number | null;
}
export const metricasSuporteQuery = {
  queryKey: ['admin', 'suporte', 'metricas'] as const,
  queryFn: () => api.get<MetricasSuporte>('/admin/suporte/metricas'),
};

/** "Acessar como": o dono entra no app como qualquer cliente ou profissional. */
export interface UsuarioAcessavel {
  id: string; nome: string; email: string; status: string; perfil_status: string | null;
}
export const acessoBuscaQuery = (q: string) => ({
  queryKey: ['admin', 'acesso', q] as const,
  queryFn: async () => (await api.get<{ usuarios: UsuarioAcessavel[] }>(`/admin/acesso?q=${encodeURIComponent(q)}`)).usuarios,
});
export const acessarComo = (usuarioId: string) =>
  api.post<{ ok: true; nome: string }>(`/admin/acesso/${encodeURIComponent(usuarioId)}`);
export const voltarAoPainel = () => api.post<{ ok: true }>('/admin/acesso/voltar');
export const usarMinhaConta = () => api.post<{ ok: true }>('/admin/acesso/minha-conta');

// --- Pessoas cadastradas (clientes e profissionais) ---

export interface PessoaAdmin {
  id: string;
  nome: string;
  email: string;
  telefone: string | null;
  status: 'ativo' | 'suspenso';
  criado_em: number;
  profissional: { status: string; nome_exibicao: string | null; slug: string | null } | null;
  suspensao: { motivo: string | null; ate: number | null; desde: number } | null;
}

export interface FiltroPessoas {
  q: string;
  tipo: 'todos' | 'cliente' | 'profissional';
  status: 'todos' | 'ativo' | 'suspenso';
  pagina: number;
}

export const pessoasAdminQuery = (f: FiltroPessoas) => ({
  queryKey: ['admin', 'pessoas', f] as const,
  queryFn: () => api.get<{ pessoas: PessoaAdmin[]; pagina: number; tem_mais: boolean }>(
    `/admin/pessoas?${new URLSearchParams({ q: f.q, tipo: f.tipo, status: f.status, pagina: String(f.pagina) })}`,
  ),
});

export const suspenderPessoa = (id: string, motivo: string, dias: number | null) =>
  api.post<{ ok: true }>(`/admin/pessoas/${id}/suspender`, { motivo, dias });

export const reativarPessoa = (id: string, motivo: string) =>
  api.post<{ ok: true }>(`/admin/pessoas/${id}/reativar`, { motivo });

// ---------------------------------------------------------------------------
// Sugestões
// ---------------------------------------------------------------------------

export interface SugestaoAdmin {
  id: string;
  texto: string;
  contexto: 'cliente' | 'profissional';
  status: StatusSugestao;
  publica: boolean;
  titulo_publico: string | null;
  resposta: string | null;
  apoios: number;
  criado_em: number;
  autor_nome: string | null;
}

export const sugestoesAdminQuery = (status: StatusSugestao | null) => ({
  queryKey: ['admin', 'sugestoes', status] as const,
  queryFn: () => api.get<{ sugestoes: SugestaoAdmin[] }>(
    `/admin/sugestoes${status ? `?status=${status}` : ''}`,
  ),
});

export const decidirSugestao = (id: string, d: {
  status: StatusSugestao; publica: boolean; titulo_publico: string | null; resposta: string | null;
}) => api.patch<{ ok: true }>(`/admin/sugestoes/${id}`, d);
